import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Car, Calendar, Search, X, Plus, Upload, FileText, Camera } from 'lucide-react';
import styles from './styles/SellCarWizard.module.css';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  BRANDS_MODELS,
  POPULAR_BRANDS,
  getBrandLogo,
  getAllBrands,
} from '../../data/brandsModels';

const CITIES        = ['Indore','Dewas','Mhow','Ujjain','Pithampur'];
const FUEL_TYPES    = ['Petrol','Diesel','CNG','Electric','Hybrid','LPG'];
const TRANSMISSIONS = ['Manual','Automatic','AMT','CVT','DCT'];
const KM_RANGES     = ['10,001 - 30,000 km','30,001 - 60,000 km','60,001 - 1,00,000 km','1,00,001 - 1,50,000 km','1,50,001+ km'];
const OWNERSHIP     = ['1st owner','2nd owner','3rd owner','4th owner','Beyond 4th owner'];
const TIME_LABELS   = { morning: '9 AM – 12 PM', afternoon: '12 PM – 4 PM', evening: '4 PM – 7 PM' };
const CURRENT_YEAR  = new Date().getFullYear();
const YEARS         = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR - i);

const STEP_GROUPS = [
  { label: 'Online Estimate',    steps: [1,2,3,4,5,6,7,8,9] },
  { label: 'Documents & Photos', steps: [10,11] },
  { label: 'Car Inspection',     steps: [12,13] },
  { label: 'Instant Payment',    steps: [14] },
];

function getUserDraftKey() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    if (user?.id)    return `aucwini_sell_draft_uid_${user.id}`;
    if (user?.phone) return `aucwini_sell_draft_phone_${user.phone}`;
  } catch {}
  return null;
}
function clearDraftStorage() {
  localStorage.removeItem('aucwini_sell_draft');
  const key = getUserDraftKey();
  if (key) localStorage.removeItem(key);
}

const TAG_STEP_MAP = [
  { field: 'registration_number', label: 'Reg No',      step: 1 },
  { field: 'car_brand',           label: 'Brand',        step: 2 },
  { field: 'model',               label: 'Model',        step: 2 },
  { field: 'year',                label: 'Year',         step: 3 },
  { field: 'fuel_type',           label: 'Fuel',         step: 4 },
  { field: 'transmission',        label: 'Transmission', step: 5 },
  { field: 'km_range',            label: 'KM',           step: 6 },
  { field: 'ownership',           label: 'Owner',        step: 7 },
];

function kmRangeToNumber(range) {
  if (!range) return 50000;
  const match = range.match(/[\d,]+/);
  if (!match) return 50000;
  const n = parseInt(match[0].replace(/,/g, ''));
  return isNaN(n) ? 50000 : n;
}
function formatPrice(p) {
  if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
  if (p >= 100000)   return `₹${(p / 100000).toFixed(1)}L`;
  return `₹${p.toLocaleString('en-IN')}`;
}
function estimatePrice(brand, year, kmRange, ownership) {
  if (!brand || !year) return null;
  const yearDiff = CURRENT_YEAR - parseInt(year);
  const km = kmRangeToNumber(kmRange);
  const premium = ['Mercedes-Benz','BMW','Audi','Volvo','Jaguar','Land Rover','Porsche','Maserati','Lamborghini','Ferrari','Rolls-Royce','Bentley','Aston Martin'];
  const mid     = ['Kia','MG Motor','Toyota','Honda','Volkswagen','Skoda','Jeep','Tesla'];
  let base = premium.includes(brand) ? 2500000 : mid.includes(brand) ? 1200000 : 700000;
  const dep = Math.min(yearDiff * (yearDiff <= 5 ? 0.15 : 0.10), 0.80);
  let price = base * (1 - dep);
  if (km > 100000) price *= 0.85;
  else if (km > 50000) price *= 0.92;
  else if (km < 20000) price *= 1.05;
  const ownerIdx = OWNERSHIP.indexOf(ownership);
  if (ownerIdx === 1) price *= 0.90;
  else if (ownerIdx >= 2) price *= 0.82;
  return { min: Math.round(price * 0.90 / 1000) * 1000, max: Math.round(price * 1.10 / 1000) * 1000 };
}
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const BLANK_CAR = {
  car_brand: '', model: '', year: '', fuel_type: '',
  transmission: '', km_range: '', ownership: '',
  sell_timeline: '', registration_number: '', price_expectation: 500000,
};
const BLANK_INSP = (cityName) => ({
  inspection_type: 'home', seller_address: '', branch_location: '',
  preferred_date: '', preferred_time: 'morning',
  contact_person: '', contact_phone: '', city: cityName, special_instructions: '',
  expected_price: '',
  price_negotiable: 'Yes, Negotiable',
  loan_on_car: 'No Loan',
  accident_history: 'No Accident',
  extra_details: '',
  full_name: '',
  email: '',
  whatsapp_number: '',
  current_address: '',
  pincode: '',
  account_holder_name: '',
  bank_name: '',
  account_number: '',
  ifsc_code: '',
});

const BLANK_DOCS = {
  rc_front: null, rc_back: null, insurance: null, puc: null,
  aadhar_front: null, aadhar_back: null, pan_card: null, service_history: null,
};

const BLANK_PHOTOS = {
  front_view: null, rear_view: null, left_side: null, right_side: null,
  front_angle: null, rear_angle: null, dashboard: null, odometer: null,
  rear_seats: null, engine_bay: null, boot_dicky: null, any_damage: null,
};

// ─── File Upload Box ───────────────────────────────────────────────────────────
function FileUploadBox({ label, sublabel, icon, file, onChange, required = false, accept = "image/*,.pdf" }) {
  const inputRef = useRef(null);
  const isUploaded = !!file;
  return (
    <div className={styles.uploadBox} onClick={() => inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => onChange(e.target.files[0] || null)} />
      {isUploaded ? (
        <div className={styles.uploadedState}>
          <div className={styles.uploadedIcon}>✅</div>
          <div className={styles.uploadedName}>{file.name?.length > 20 ? file.name.slice(0,20) + '…' : file.name}</div>
          <button className={styles.removeFileBtn} onClick={e => { e.stopPropagation(); onChange(null); }}>×</button>
        </div>
      ) : (
        <div className={styles.uploadEmptyState}>
          <div className={styles.uploadEmoji}>{icon}</div>
          <div className={styles.uploadLabel}>{label}</div>
          <div className={styles.uploadSub}>{sublabel || 'JPG, PNG or PDF • Max 5MB'}</div>
        </div>
      )}
      {required && !isUploaded && <div className={styles.uploadRequired}>*</div>}
    </div>
  );
}

// ─── Photo Upload Box ──────────────────────────────────────────────────────────
function PhotoUploadBox({ label, sublabel, icon, file, onChange, required = false }) {
  const inputRef = useRef(null);
  const isUploaded = !!file;
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (file) { const url = URL.createObjectURL(file); setPreviewUrl(url); return () => URL.revokeObjectURL(url); }
    else setPreviewUrl(null);
  }, [file]);
  return (
    <div className={styles.photoBox} onClick={() => inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => onChange(e.target.files[0] || null)} />
      {isUploaded && previewUrl ? (
        <div className={styles.photoPreviewWrap}>
          <img src={previewUrl} alt={label} className={styles.photoPreview} />
          <button className={styles.removePhotoBtn} onClick={e => { e.stopPropagation(); onChange(null); }}>×</button>
          <div className={styles.photoLabelOverlay}>{label}</div>
        </div>
      ) : (
        <div className={styles.photoEmptyState}>
          <div className={styles.photoEmoji}>{icon}</div>
          <div className={styles.photoLabel}>{label}{required && <span className={styles.reqStar}>*</span>}</div>
          {sublabel && <div className={styles.photoSub}>{sublabel}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Step 10: Documents ────────────────────────────────────────────────────────
function DocumentsStep({ docs, onChange }) {
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Upload your <strong>documents</strong></h2>
      <p className={styles.stepSub}>Upload vehicle and owner documents.</p>
      <div className={styles.docSectionHeader}><span className={styles.docSectionIcon}>🚗</span><span className={styles.docSectionTitle}>VEHICLE DOCUMENTS</span></div>
      <div className={styles.docGrid}>
        <div><div className={styles.docFieldLabel}>RC BOOK (FRONT) <span className={styles.reqStar}>*</span></div><FileUploadBox label="Upload RC Book." icon="📋" file={docs.rc_front} onChange={f => onChange('rc_front', f)} required /></div>
        <div><div className={styles.docFieldLabel}>RC BOOK (BACK)</div><FileUploadBox label="RC Back Side" icon="📋" file={docs.rc_back} onChange={f => onChange('rc_back', f)} /></div>
        <div><div className={styles.docFieldLabel}>INSURANCE CERTIFICATE <span className={styles.reqStar}>*</span></div><FileUploadBox label=" Upload Insurance" icon="🛡️" file={docs.insurance} onChange={f => onChange('insurance', f)} required /></div>
        <div><div className={styles.docFieldLabel}>PUC CERTIFICATE <span className={styles.reqStar}>*</span></div><FileUploadBox label="PUC Certificate" icon="📄" file={docs.puc} onChange={f => onChange('puc', f)} required /></div>
      </div>
      <div className={styles.docSectionHeader} style={{ marginTop: 28 }}><span className={styles.docSectionIcon}>👤</span><span className={styles.docSectionTitle}>OWNER DOCUMENTS</span></div>
      <div className={styles.docGrid}>
        <div><div className={styles.docFieldLabel}>AADHAR CARD (FRONT) <span className={styles.reqStar}>*</span></div><FileUploadBox label="Aadhar Front" icon="🪪" file={docs.aadhar_front} onChange={f => onChange('aadhar_front', f)} required /></div>
        <div><div className={styles.docFieldLabel}>AADHAR CARD (BACK) <span className={styles.reqStar}>*</span></div><FileUploadBox label="Aadhar Back" icon="🪪" file={docs.aadhar_back} onChange={f => onChange('aadhar_back', f)} required /></div>
        <div><div className={styles.docFieldLabel}>PAN CARD <span className={styles.reqStar}>*</span></div><FileUploadBox label="PAN Card" icon="💳" file={docs.pan_card} onChange={f => onChange('pan_card', f)} required /></div>
        <div><div className={styles.docFieldLabel}>SERVICE HISTORY (OPTIONAL)</div><FileUploadBox label="Service Book" icon="🔧" sublabel="Optional • Max 5MB" file={docs.service_history} onChange={f => onChange('service_history', f)} /></div>
      </div>
      <div className={styles.docInfoNote}><span>If the vehicle has a loan, upload the <strong>NOC (Form 35)</strong> from the bank.</span></div>
    </div>
  );
}

// ─── Step 11: Photos ───────────────────────────────────────────────────────────
function PhotosStep({ photos, onChange }) {
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Upload <strong>car photos</strong></h2>
      <div className={styles.photoTipBox}>💡 Take photos in daylight with a clean background. Better photos help sell your vehicle faster!</div>
      <div className={styles.docSectionHeader}><span className={styles.docSectionIcon}>📷</span><span className={styles.docSectionTitle}>EXTERIOR PHOTOS</span></div>
      <div className={styles.photoGrid}>
        <div><div className={styles.docFieldLabel}>FRONT VIEW <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Front" sublabel="from front" icon="🚗" file={photos.front_view} onChange={f => onChange('front_view', f)} required /></div>
        <div><div className={styles.docFieldLabel}>REAR VIEW <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Rear" sublabel="Number plate should be visible." icon="🚗" file={photos.rear_view} onChange={f => onChange('rear_view', f)} required /></div>
        <div><div className={styles.docFieldLabel}>LEFT SIDE <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Left Side" sublabel="Full profile" icon="🚙" file={photos.left_side} onChange={f => onChange('left_side', f)} required /></div>
        <div><div className={styles.docFieldLabel}>RIGHT SIDE <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Right Side" sublabel="Full profile" icon="🚙" file={photos.right_side} onChange={f => onChange('right_side', f)} required /></div>
        <div><div className={styles.docFieldLabel}>FRONT ANGLE</div><PhotoUploadBox label="Front-Left Angle" sublabel="3/4 view" icon="📸" file={photos.front_angle} onChange={f => onChange('front_angle', f)} /></div>
        <div><div className={styles.docFieldLabel}>REAR ANGLE</div><PhotoUploadBox label="Rear-Right Angle" sublabel="3/4 view" icon="📸" file={photos.rear_angle} onChange={f => onChange('rear_angle', f)} /></div>
      </div>
      <div className={styles.docSectionHeader} style={{ marginTop: 28 }}><span className={styles.docSectionIcon}>🪑</span><span className={styles.docSectionTitle}>INTERIOR PHOTOS</span></div>
      <div className={styles.photoGrid}>
        <div><div className={styles.docFieldLabel}>DASHBOARD <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Dashboard" sublabel="Steering + controls" icon="🎛️" file={photos.dashboard} onChange={f => onChange('dashboard', f)} required /></div>
        <div><div className={styles.docFieldLabel}>ODOMETER <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Odometer Reading" sublabel="Number plate should be visible." icon="⏱️" file={photos.odometer} onChange={f => onChange('odometer', f)} required /></div>
        <div><div className={styles.docFieldLabel}>REAR SEATS <span className={styles.reqStar}>*</span></div><PhotoUploadBox label="Rear Seats" sublabel="Condition should be visible." icon="💺" file={photos.rear_seats} onChange={f => onChange('rear_seats', f)} required /></div>
        <div><div className={styles.docFieldLabel}>ENGINE BAY</div><PhotoUploadBox label="Engine (Bonnet Open)" sublabel="Optional" icon="🔧" file={photos.engine_bay} onChange={f => onChange('engine_bay', f)} /></div>
        <div><div className={styles.docFieldLabel}>BOOT / DICKY</div><PhotoUploadBox label="Boot Space" sublabel="Optional" icon="🎒" file={photos.boot_dicky} onChange={f => onChange('boot_dicky', f)} /></div>
        <div><div className={styles.docFieldLabel}>ANY DAMAGE (IF ANY)</div><PhotoUploadBox label="Damage Photos" sublabel="If there is any scratch or dent." icon="⚠️" file={photos.any_damage} onChange={f => onChange('any_damage', f)} /></div>
      </div>
    </div>
  );
}

// ─── Step 12: Inspection + All Details ────────────────────────────────────────
function InspectionStep({ formData, onChange, city, validationErrors = {} }) {
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i + 1);
    return { value: d.toISOString().split('T')[0], label: d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) };
  });
  const errClass = (field) => validationErrors[field] ? styles.inputError : '';

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Schedule <strong>car inspection</strong> + fill details</h2>
      {Object.keys(validationErrors).length > 0 && (
        <div className={styles.validationBanner}>
          <span>⚠️</span>
          <div><strong>Some fields are incomplete.</strong> Please check and complete the  <span style={{ color: '#dc2626' }}>highlighted red</span>  fields below.
</div>
        </div>
      )}

      {/* Inspection Type */}
      <div className={styles.inspTypeRow}>
        {['home','branch'].map(type => (
          <button key={type} className={`${styles.inspTypeCard} ${formData.inspection_type === type ? styles.selected : ''}`} onClick={() => onChange('inspection_type', type)}>
            <span className={styles.inspTypeIcon}>{type === 'home' ? '🏠' : '🏢'}</span>
            <strong>{type === 'home' ? 'Home Inspection' : 'Branch Inspection'}</strong>
            <span className={styles.inspTypeFee}>₹{type === 'home' ? '500' : '250'}</span>
            <p>{type === 'home' ? 'Inspector visits your location' : 'Visit our nearest branch'}</p>
          </button>
        ))}
      </div>

      {/* Date */}
      <div className={styles.fieldGroup} id="ve-preferred_date">
        <label>Select Date <span className={styles.reqStar}>*</span>{validationErrors.preferred_date && <span className={styles.fieldErrorMsg}> — {validationErrors.preferred_date}</span>}</label>
        <div className={styles.dateRow}>
          {dates.map(d => (<button key={d.value} className={`${styles.dateCard} ${formData.preferred_date === d.value ? styles.selected : ''}`} onClick={() => onChange('preferred_date', d.value)}>{d.label}</button>))}
        </div>
      </div>

      {/* Time */}
      <div className={styles.fieldGroup}>
        <label>Select Time Slot</label>
        <div className={styles.timeRow}>
          {['morning','afternoon','evening'].map(s => (<button key={s} className={`${styles.timeCard} ${formData.preferred_time === s ? styles.selected : ''}`} onClick={() => onChange('preferred_time', s)}>{TIME_LABELS[s]}</button>))}
        </div>
      </div>

      {/* Address / Branch */}
      {formData.inspection_type === 'home' ? (
        <div className={styles.fieldGroup} id="ve-seller_address">
          <label>Your Address <span className={styles.reqStar}>*</span>{validationErrors.seller_address && <span className={styles.fieldErrorMsg}> — {validationErrors.seller_address}</span>}</label>
          <textarea className={`${styles.textarea} ${errClass('seller_address')}`} placeholder="Enter your complete address" value={formData.seller_address || ''} onChange={e => onChange('seller_address', e.target.value)} rows={3} />
        </div>
      ) : (
        <div className={styles.fieldGroup} id="ve-branch_location">
          <label>Select Branch <span className={styles.reqStar}>*</span>{validationErrors.branch_location && <span className={styles.fieldErrorMsg}> — {validationErrors.branch_location}</span>}</label>
          <select className={`${styles.select} ${errClass('branch_location')}`} value={formData.branch_location || ''} onChange={e => onChange('branch_location', e.target.value)}>
            <option value="">Choose a branch</option>
            <option value={`Main Branch - ${city}`}>Main Branch — {city}</option>
            <option value={`Service Center - ${city}`}>Service Center — {city}</option>
          </select>
        </div>
      )}

      {/* City */}
      <div className={styles.fieldGroup} id="ve-city">
        <label>City <span className={styles.reqStar}>*</span>{validationErrors.city && <span className={styles.fieldErrorMsg}> — {validationErrors.city}</span>}</label>
        <select className={`${styles.select} ${errClass('city')}`} value={formData.city || ''} onChange={e => onChange('city', e.target.value)}>
          <option value="">Select city</option>
          {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Price */}
      <div className={styles.sectionDivider}><span className={styles.sectionDividerIcon}>💰</span><span className={styles.sectionDividerText}>PRICE DETAILS</span></div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup} id="ve-expected_price">
          <label>Expected Price (₹) <span className={styles.reqStar}>*</span>{validationErrors.expected_price && <span className={styles.fieldErrorMsg}> — {validationErrors.expected_price}</span>}</label>
          <input className={`${styles.input} ${errClass('expected_price')}`} type="number" placeholder="345000" value={formData.expected_price || ''} onChange={e => onChange('expected_price', e.target.value)} />
        </div>
        <div className={styles.fieldGroup}>
          <label>Price Negotiable?</label>
          <select className={styles.select} value={formData.price_negotiable || 'Yes, Negotiable'} onChange={e => onChange('price_negotiable', e.target.value)}>
            <option value="Yes, Negotiable">Yes, Negotiable</option>
            <option value="Fixed Price">Fixed Price</option>
            <option value="Slightly Negotiable">Slightly Negotiable</option>
          </select>
        </div>
      </div>

      {/* Additional Info */}
      <div className={styles.sectionDivider}><span className={styles.sectionDividerIcon}>📋</span><span className={styles.sectionDividerText}>ADDITIONAL INFO</span></div>
      <div className={styles.fieldGroup}>
        <label>Loan on Car?</label>
        <select className={styles.select} value={formData.loan_on_car || 'No Loan'} onChange={e => onChange('loan_on_car', e.target.value)}>
          <option value="No Loan">No Loan</option>
          <option value="Yes, Loan Pending — NOC Available">Yes, Loan Pending — NOC Available</option>
          <option value="Loan Closed">Loan Closed</option>
        </select>
      </div>
      <div className={styles.fieldGroup}>
        <label>Accident History</label>
        <select className={styles.select} value={formData.accident_history || 'No Accident'} onChange={e => onChange('accident_history', e.target.value)}>
          <option value="No Accident">No Accident</option>
          <option value="Minor Accident">Minor Accident</option>
          <option value="Major Accident">Major Accident</option>
        </select>
      </div>
      <div className={styles.fieldGroup}>
        <label>Extra Details / Modifications</label>
        <textarea className={styles.textarea} placeholder="Mention any extra accessories or modifications." value={formData.extra_details || ''} onChange={e => onChange('extra_details', e.target.value)} rows={3} />
      </div>

      {/* Personal Details */}
      <div className={styles.sectionDivider}><span className={styles.sectionDividerIcon}>📝</span><span className={styles.sectionDividerText}>PERSONAL DETAILS</span></div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup} id="ve-full_name">
          <label>Full Name <span className={styles.reqStar}>*</span>{validationErrors.full_name && <span className={styles.fieldErrorMsg}> — {validationErrors.full_name}</span>}</label>
          <input className={`${styles.input} ${errClass('full_name')}`} placeholder="As per RC" value={formData.full_name || ''} onChange={e => onChange('full_name', e.target.value)} />
        </div>
        <div className={styles.fieldGroup} id="ve-contact_phone">
          <label>Mobile Number <span className={styles.reqStar}>*</span>{validationErrors.contact_phone && <span className={styles.fieldErrorMsg}> — {validationErrors.contact_phone}</span>}</label>
          <input className={`${styles.input} ${errClass('contact_phone')}`} placeholder="+91 98765 43210" maxLength={10} value={formData.contact_phone || ''} onChange={e => onChange('contact_phone', e.target.value.replace(/\D/g,''))} />
        </div>
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup} id="ve-email">
          <label>Email ID <span className={styles.reqStar}>*</span>{validationErrors.email && <span className={styles.fieldErrorMsg}> — {validationErrors.email}</span>}</label>
          <input className={`${styles.input} ${errClass('email')}`} type="email" placeholder="your@email.com" value={formData.email || ''} onChange={e => onChange('email', e.target.value)} />
        </div>
        <div className={styles.fieldGroup}>
          <label>WhatsApp Number</label>
          <input className={styles.input} placeholder="Same as mobile?" maxLength={10} value={formData.whatsapp_number || ''} onChange={e => onChange('whatsapp_number', e.target.value.replace(/\D/g,''))} />
        </div>
      </div>
      <div className={styles.fieldGroup} id="ve-current_address">
        <label>Current Address <span className={styles.reqStar}>*</span>{validationErrors.current_address && <span className={styles.fieldErrorMsg}> — {validationErrors.current_address}</span>}</label>
        <textarea className={`${styles.textarea} ${errClass('current_address')}`} placeholder="Complete home address." value={formData.current_address || ''} onChange={e => onChange('current_address', e.target.value)} rows={3} />
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label>City <span className={styles.reqStar}>*</span></label>
          <input className={styles.input} placeholder="Indore / Bhopal..." value={formData.city || ''} onChange={e => onChange('city', e.target.value)} />
        </div>
        <div className={styles.fieldGroup} id="ve-pincode">
          <label>Pincode <span className={styles.reqStar}>*</span>{validationErrors.pincode && <span className={styles.fieldErrorMsg}> — {validationErrors.pincode}</span>}</label>
          <input className={`${styles.input} ${errClass('pincode')}`} placeholder="452001" maxLength={6} value={formData.pincode || ''} onChange={e => onChange('pincode', e.target.value.replace(/\D/g,''))} />
        </div>
      </div>

      {/* Bank Details */}
      <div className={styles.sectionDivider}><span className={styles.sectionDividerIcon}>🏦</span><span className={styles.sectionDividerText}>BANK DETAILS (FOR PAYMENT)</span></div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup} id="ve-account_holder_name">
          <label>Account Holder Name <span className={styles.reqStar}>*</span>{validationErrors.account_holder_name && <span className={styles.fieldErrorMsg}> — {validationErrors.account_holder_name}</span>}</label>
          <input className={`${styles.input} ${errClass('account_holder_name')}`} placeholder="As per bank" value={formData.account_holder_name || ''} onChange={e => onChange('account_holder_name', e.target.value)} />
        </div>
        <div className={styles.fieldGroup} id="ve-bank_name">
          <label>Bank Name <span className={styles.reqStar}>*</span>{validationErrors.bank_name && <span className={styles.fieldErrorMsg}> — {validationErrors.bank_name}</span>}</label>
          <input className={`${styles.input} ${errClass('bank_name')}`} placeholder="SBI / HDFC / ICICI..." value={formData.bank_name || ''} onChange={e => onChange('bank_name', e.target.value)} />
        </div>
      </div>
      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup} id="ve-account_number">
          <label>Account Number <span className={styles.reqStar}>*</span>{validationErrors.account_number && <span className={styles.fieldErrorMsg}> — {validationErrors.account_number}</span>}</label>
          <input className={`${styles.input} ${errClass('account_number')}`} placeholder="XXXX XXXX XXXX" value={formData.account_number || ''} onChange={e => onChange('account_number', e.target.value.replace(/\D/g,''))} />
        </div>
        <div className={styles.fieldGroup} id="ve-ifsc_code">
          <label>IFSC Code <span className={styles.reqStar}>*</span>{validationErrors.ifsc_code && <span className={styles.fieldErrorMsg}> — {validationErrors.ifsc_code}</span>}</label>
          <input className={`${styles.input} ${errClass('ifsc_code')}`} placeholder="SBIN0001234" value={formData.ifsc_code || ''} onChange={e => onChange('ifsc_code', e.target.value.toUpperCase())} />
          <div className={styles.fieldHint}> e.g. SBIN0001234</div>
        </div>
      </div>
      <div className={styles.bankSecureNote}>🔒 Your bank details are 100% secure. Payment will be sent to your account after the deal is completed.</div>
      <div className={styles.infoBox}>
        <h4>📋 Keep Ready</h4>
        <ul>
          <li>Original RC (Registration Certificate)</li>
          <li>Valid Insurance Documents</li>
          <li>PAN Card</li>
          <li>Service History (if available)</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Step 1 ────────────────────────────────────────────────────────────────────
function RegistrationFirstStep({ value, onChange, onNext }) {
  const [input, setInput] = useState(value || '');
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Enter your car's <strong>registration number</strong></h2>
      <p className={styles.stepSub}>e.g. MP09AB1234 — helps us fetch car details automatically</p>
      <input className={styles.bigInput} placeholder="e.g. MP09AB1234" value={input}
        onChange={(e) => { const v = e.target.value.toUpperCase(); setInput(v); onChange(v); }} maxLength={11} autoFocus />
      <button className={styles.primaryBtn} onClick={onNext} style={{ marginTop: '20px', width: '100%' }}>Get Your Car Price →</button>
    </div>
  );
}

// ─── Step 2: Brand & Model ─────────────────────────────────────────────────────
function BrandModelStep({ brandValue, modelValue, onSelect }) {
  const [globalSearch, setGlobalSearch]       = useState('');
  const [selectedBrand, setSelectedBrand]     = useState(null);
  const [showPopup, setShowPopup]             = useState(false);
  const [modelSearch, setModelSearch]         = useState('');
  const [justSelected, setJustSelected]       = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualBrand, setManualBrand]         = useState('');
  const [manualModel, setManualModel]         = useState('');
  const [manualError, setManualError]         = useState('');
  const popupRef  = useRef(null);
  const allBrands = getAllBrands();

  const isSearching  = globalSearch.trim().length >= 1;
  const searchLower  = globalSearch.toLowerCase().trim();
  const brandMatches = isSearching ? allBrands.filter(b => b.toLowerCase().includes(searchLower)) : [];
  const modelMatches = globalSearch.trim().length >= 2
    ? allBrands.flatMap(brand => (BRANDS_MODELS[brand] || []).filter(m => m.toLowerCase().includes(searchLower)).map(m => ({ brand, model: m })))
    : [];
  const noResults = isSearching && brandMatches.length === 0 && modelMatches.length === 0;
  const popularSection = !isSearching ? POPULAR_BRANDS.filter(b => allBrands.includes(b)) : [];
  const otherSection   = !isSearching ? allBrands.filter(b => !POPULAR_BRANDS.includes(b)) : [];
  const allModels      = selectedBrand ? (BRANDS_MODELS[selectedBrand] || []) : [];
  const filteredModels = modelSearch.trim() ? allModels.filter(m => m.toLowerCase().includes(modelSearch.toLowerCase())) : allModels;
  const noModelResults = modelSearch.trim().length >= 1 && filteredModels.length === 0;

  function handleBrandClick(brand) { setSelectedBrand(brand); setModelSearch(''); setShowPopup(true); }
  function handleDirectSelect(brand, model) { setJustSelected(model); setTimeout(() => { onSelect(brand, model); setShowPopup(false); setJustSelected(null); setModelSearch(''); }, 320); }
  function closePopup() { setShowPopup(false); setModelSearch(''); }
  function openManualFromSearch() { setManualBrand(globalSearch.trim()); setManualModel(''); setManualError(''); setShowManualModal(true); }
  function openManualFromPopup()  { setManualBrand(selectedBrand || ''); setManualModel(modelSearch.trim()); setManualError(''); setShowManualModal(true); }
  function openManualBlank()      { setManualBrand(''); setManualModel(''); setManualError(''); setShowManualModal(true); }
  function handleManualSubmit() {
    const brand = manualBrand.trim(); const model = manualModel.trim();
    if (!brand) { setManualError('Please enter a brand name'); return; }
    if (!model) { setManualError('Please enter a model name'); return; }
    setManualError(''); onSelect(brand, model); setShowManualModal(false); setShowPopup(false); setManualBrand(''); setManualModel('');
  }

  useEffect(() => {
    if (!showPopup && !showManualModal) return;
    function onKey(e) { if (e.key === 'Escape') { if (showManualModal) { setShowManualModal(false); return; } closePopup(); } }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPopup, showManualModal]);

  function BrandCard({ brand }) {
    const isSelected = brandValue === brand;
    return (
      <button className={`${styles.brandCardCompact} ${isSelected ? styles.selected : ''}`} onClick={() => handleBrandClick(brand)} title={`Select ${brand} model`}>
        <div className={`${styles.brandLogoCompact} ${isSelected ? styles.brandLogoSelected : ''}`}>{getBrandLogo(brand)}</div>
        <span className={styles.brandNameCompact}>{brand}</span>
        <span className={`${styles.brandArrow} ${isSelected ? styles.brandArrowSelected : ''}`}>›</span>
      </button>
    );
  }

  const modelPopupPortal = showPopup ? ReactDOM.createPortal(
    <div className={styles.modelPopupOverlay} onClick={closePopup}>
      <div className={styles.modelPopup} onClick={e => e.stopPropagation()} ref={popupRef}>
        <div className={styles.modelPopupHeader}>
          <div className={styles.modelPopupBrandInfo}>
            <div className={styles.modelPopupLogo}>{getBrandLogo(selectedBrand)}</div>
            <div><div className={styles.modelPopupBrandName}>{selectedBrand}</div><div className={styles.modelPopupSub}>{allModels.length} models available — tap to select</div></div>
          </div>
          <button className={styles.modelPopupClose} onClick={closePopup} aria-label="Close"><X size={16} /></button>
        </div>
        <div className={styles.modelPopupSearchBar}>
          <Search size={14} />
          <input placeholder={`Search ${selectedBrand} models…`} value={modelSearch} onChange={e => setModelSearch(e.target.value)} autoFocus />
          {modelSearch && <button className={styles.searchClearBtn} onClick={() => setModelSearch('')} aria-label="Clear"><X size={12} /></button>}
        </div>
        <div className={styles.modelPopupGrid}>
          {filteredModels.map((model, i) => {
            const isActive  = modelValue === model && brandValue === selectedBrand;
            const isShimmer = justSelected === model;
            return (
              <button key={model} className={`${styles.modelPopupCard} ${isActive ? styles.modelPopupCardActive : ''} ${isShimmer ? styles.modelPopupCardShimmer : ''}`}
                onClick={() => handleDirectSelect(selectedBrand, model)} style={{ animationDelay: `${i * 0.03}s` }}>
                <span className={styles.modelPopupIcon}>🚗</span>
                <span className={styles.modelPopupName}>{model}</span>
                {isActive && <span className={styles.modelPopupCheck}>✓</span>}
              </button>
            );
          })}
          {noModelResults && <div className={styles.noModelResultsBox}><p>No model found for "<strong>{modelSearch}</strong>"</p></div>}
        </div>
        <div className={styles.modelPopupFooter}>
          <button className={styles.modelPopupManualBtn} onClick={openManualFromPopup}><Plus size={14} />Can't find your model? Add it manually</button>
        </div>
      </div>
    </div>, document.body) : null;

  const manualModalPortal = showManualModal ? ReactDOM.createPortal(
    <div className={styles.modelPopupOverlay} onClick={() => setShowManualModal(false)}>
      <div className={styles.manualModal} onClick={e => e.stopPropagation()}>
        <div className={styles.manualModalHeader}>
          <div><div className={styles.manualModalTitle}>🚗 Add Your Car Manually</div><div className={styles.manualModalSub}>Enter your car brand &amp; model</div></div>
          <button className={styles.modelPopupClose} onClick={() => setShowManualModal(false)}><X size={16} /></button>
        </div>
        <div className={styles.manualModalBody}>
          <div className={styles.manualField}>
            <label className={styles.manualLabel}>Car Brand *</label>
            <input className={`${styles.manualInput} ${manualError && !manualBrand.trim() ? styles.manualInputError : ''}`} placeholder="e.g. Maruti Suzuki, Tata, Honda…" value={manualBrand} autoFocus={!manualBrand} onChange={e => { setManualBrand(e.target.value); setManualError(''); }} />
          </div>
          <div className={styles.manualField}>
            <label className={styles.manualLabel}>Car Model *</label>
            <input className={`${styles.manualInput} ${manualError && !manualModel.trim() ? styles.manualInputError : ''}`} placeholder="e.g. Swift, Nexon, City…" value={manualModel} onChange={e => { setManualModel(e.target.value); setManualError(''); }} />
          </div>
          {manualError && <p className={styles.manualError}>⚠️ {manualError}</p>}
          <div className={styles.manualNote}>💡 Your car details will be saved as entered.</div>
        </div>
        <div className={styles.manualModalFooter}>
          <button className={styles.manualCancelBtn} onClick={() => setShowManualModal(false)}>Cancel</button>
          <button className={styles.manualSubmitBtn} onClick={handleManualSubmit} disabled={!manualBrand.trim() || !manualModel.trim()}>✓ Use This Car</button>
        </div>
      </div>
    </div>, document.body) : null;

  return (
    <>
      <div className={styles.stepContent}>
        <h2 className={styles.stepQuestion}>Select <strong>brand &amp; model</strong> of your car</h2>
        {brandValue && modelValue && (
          <div className={styles.selectedBrandModelBanner}>
            <div className={styles.selectedBannerLeft}>
              <div className={styles.selectedBannerLogo}>{getBrandLogo(brandValue)}</div>
              <div><div className={styles.selectedBannerBrand}>{brandValue}</div><div className={styles.selectedBannerModel}>✓ {modelValue}</div></div>
            </div>
            <button className={styles.selectedBannerChange} onClick={() => { setSelectedBrand(brandValue); setModelSearch(''); setShowPopup(true); }}>Change</button>
          </div>
        )}
        <div className={styles.searchBox}>
          <Search size={15} />
          <input placeholder="Search brand or model (e.g. Swift, Creta, BMW)…" value={globalSearch} onChange={e => setGlobalSearch(e.target.value)} autoFocus={!brandValue} />
          {globalSearch && <button className={styles.searchClearBtn} onClick={() => setGlobalSearch('')} aria-label="Clear search"><X size={13} /></button>}
        </div>
        {isSearching ? (
          <div className={styles.brandScrollArea}>
            {brandMatches.length > 0 && (<><div className={styles.brandSectionLabel}>🚗 Brands</div><div className={styles.brandGridCompact}>{brandMatches.map(b => <BrandCard key={b} brand={b} />)}</div></>)}
            {modelMatches.length > 0 && (
              <><div className={styles.brandSectionLabel} style={{ marginTop: brandMatches.length > 0 ? 14 : 0 }}>🔍 Models found</div>
              <div className={styles.modelSearchResultsGrid}>
                {modelMatches.map(({ brand, model }) => {
                  const isActive = brandValue === brand && modelValue === model;
                  return (
                    <button key={`${brand}__${model}`} className={`${styles.modelSearchResultCard} ${isActive ? styles.modelSearchResultCardActive : ''}`} onClick={() => handleDirectSelect(brand, model)}>
                      <div className={styles.modelSearchResultLogo}>{getBrandLogo(brand)}</div>
                      <div className={styles.modelSearchResultInfo}><span className={styles.modelSearchResultBrand}>{brand}</span><span className={styles.modelSearchResultModel}>{model}</span></div>
                      {isActive && <span className={styles.modelSearchCheck}>✓</span>}
                    </button>
                  );
                })}
              </div></>
            )}
            {noResults && (<div className={styles.noResultsBox}><div className={styles.noResultsIcon}>🔍</div><p className={styles.noResultsText}>No brand or model found for "<strong>{globalSearch}</strong>"</p><button className={styles.manualAddBigBtn} onClick={openManualFromSearch}><Plus size={15} />Add "{globalSearch}" manually</button></div>)}
            {!noResults && (<div className={styles.manualAddLinkRow}><button className={styles.manualAddLink} onClick={openManualBlank}>+ Can't find your car? Add manually</button></div>)}
          </div>
        ) : (
          <div className={styles.brandScrollArea}>
            {popularSection.length > 0 && (<><div className={styles.brandSectionLabel}>⭐ Popular Brands</div><div className={styles.brandGridCompact}>{popularSection.map(b => <BrandCard key={b} brand={b} />)}</div>{otherSection.length > 0 && <div className={styles.brandSectionLabel} style={{ marginTop: 14 }}>All Brands</div>}</>)}
            <div className={styles.brandGridCompact}>{otherSection.map(b => <BrandCard key={b} brand={b} />)}</div>
            <div className={styles.manualAddLinkRow}><button className={styles.manualAddLink} onClick={openManualBlank}>+ Can't find your brand or model? Add manually</button></div>
          </div>
        )}
      </div>
      {modelPopupPortal}
      {manualModalPortal}
    </>
  );
}

function YearStep({ value, onChange }) {
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Select the car <strong>manufacturing year</strong></h2>
      <div className={styles.yearGrid}>{YEARS.map(y => (<button key={y} className={`${styles.yearCard} ${value === String(y) ? styles.selected : ''}`} onClick={() => onChange(String(y))}>{y}</button>))}</div>
    </div>
  );
}

function ListStep({ question, options, value, onChange }) {
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion} dangerouslySetInnerHTML={{ __html: question }} />
      <div className={styles.listOptions}>{options.map(o => (<button key={o} className={`${styles.listOption} ${value === o ? styles.selected : ''}`} onClick={() => onChange(o)}>{o}</button>))}</div>
    </div>
  );
}

function ValuationStep({ brand, model, year, kmRange, ownership, onSchedule }) {
  const est = estimatePrice(brand, year, kmRange, ownership);
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Your car's <strong>estimated value</strong></h2>
      <div className={styles.valuationCard}>
        <div className={styles.valuationBrand}>
          <div className={styles.valBrandBadge}>{brand?.slice(0,2).toUpperCase()}</div>
          <div><div className={styles.valCarName}>{brand} {model}</div><div className={styles.valCarMeta}>{year} · {kmRange} · {ownership}</div></div>
        </div>
        {est && (<div className={styles.valuationPrice}><div className={styles.valLabel}>Estimated Market Value</div><div className={styles.valRange}>{formatPrice(est.min)} – {formatPrice(est.max)}</div><div className={styles.valDisclaimer}>📊 Final price after physical inspection</div></div>)}
      </div>
      <div className={styles.valuationNote}>Schedule a <strong>free car inspection</strong> to get the exact price.</div>
      <button className={styles.primaryBtn} onClick={onSchedule}>Book Free Car Inspection →</button>
    </div>
  );
}

function LoginGate({ phone, otp, loginStep, onPhoneChange, onOtpChange, onSendOtp, onVerifyOtp, loading, error, countdown, onResend, isNewUser }) {
  const refs = useRef([]);
  function handleOtpInput(i, val) {
    const clean = val.replace(/\D/g,'').slice(-1);
    const next = [...otp]; next[i] = clean; onOtpChange(next);
    if (clean && i < 5) refs.current[i+1]?.focus();
  }
  function handleKey(i, e) { if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i-1]?.focus(); }
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>{loginStep === 'phone' ? <>Enter your <strong>mobile number</strong> to continue</> : isNewUser ? <>Create your <strong>account</strong></> : <>Welcome back! Enter the <strong>OTP</strong></>}</h2>
      {loginStep === 'phone' ? (
        <>
          <p className={styles.stepSub}>We'll send an OTP to verify your number</p>
          <div className={styles.phoneRow}><span className={styles.phoneCC}>🇮🇳 +91</span><input className={styles.phoneInput} type="tel" maxLength={10} placeholder="Mobile Number" value={phone} onChange={e => onPhoneChange(e.target.value.replace(/\D/g,'').slice(0,10))} onKeyDown={e => e.key === 'Enter' && phone.length === 10 && onSendOtp()} autoFocus /></div>
          {error && <p className={styles.errText}>{error}</p>}
          <button className={styles.primaryBtn} onClick={onSendOtp} disabled={loading || phone.length !== 10}>{loading ? 'Sending…' : 'Get OTP →'}</button>
        </>
      ) : (
        <>
          <p className={styles.stepSub}>OTP sent to +91 {phone} — <button className={styles.linkBtn} onClick={() => onPhoneChange('')}>Change</button></p>
          {isNewUser && <div className={styles.newUserBadge}>✨ New account will be created automatically</div>}
          <div className={styles.otpRow}>{otp.map((d,i) => (<input key={i} ref={el => refs.current[i]=el} className={`${styles.otpBox} ${d ? styles.filled : ''}`} type="tel" maxLength={1} value={d} onChange={e => handleOtpInput(i, e.target.value)} onKeyDown={e => handleKey(i, e)} />))}</div>
          {error && <p className={styles.errText}>{error}</p>}
          <button className={styles.primaryBtn} onClick={onVerifyOtp} disabled={loading || otp.join('').length !== 6}>{loading ? 'Verifying…' : isNewUser ? 'Create Account & Continue →' : 'Login & Continue →'}</button>
          <div className={styles.resendRow}>{countdown > 0 ? <span>Resend OTP in <strong>{countdown}s</strong></span> : <button className={styles.linkBtn} onClick={onResend}>Resend OTP</button>}</div>
        </>
      )}
    </div>
  );
}

function PaymentStep({ inspectionType, inspectionFee, inspectionId, onPaymentSuccess, onBack }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  useEffect(() => { loadRazorpayScript(); }, []);
  async function handlePay() {
    if (!inspectionId) { setError('Inspection booking ID is missing. Please go back and try again.'); return; }
    setLoading(true); setError('');
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) { setError('Payment gateway could not load.'); setLoading(false); return; }
      const { data } = await api.post('/payments/initiate', { inspection_id: inspectionId });
      if (!data || !data.razorpay_order_id) { setError('Failed to create payment order.'); setLoading(false); return; }
      const rzp = new window.Razorpay({
        key: data.razorpay_key, amount: data.amount_paisa, currency: 'INR', order_id: data.razorpay_order_id,
        name: 'Caryugo', description: 'Car Inspection Fee', theme: { color: '#003566' },
        handler: async (response) => {
          try {
            await api.post(`/payments/${data.payment_id}/confirm`, { razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature, razorpay_order_id: data.razorpay_order_id });
            localStorage.setItem('aucwini_last_payment', JSON.stringify({ razorpay_payment_id: response.razorpay_payment_id, amount: inspectionFee, method: 'online', status: 'success', paid_at: new Date().toISOString() }));
            setLoading(false); onPaymentSuccess(response.razorpay_payment_id);
          } catch (confirmErr) { setError(confirmErr.response?.data?.message || confirmErr.message || 'Payment verification failed.'); setLoading(false); }
        },
        modal: { ondismiss: () => setLoading(false), escape: true },
      });
      rzp.on('payment.failed', (r) => { setError(`Payment failed: ${r.error?.description || 'Unknown error'}`); setLoading(false); });
      rzp.open();
    } catch (e) { setError(e.response?.data?.message || e.message || 'Failed to initiate payment.'); setLoading(false); }
  }
  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepQuestion}>Pay <strong>inspection fee</strong> to confirm booking</h2>
      {!inspectionId && <div className={styles.apiError}>⚠️ Inspection not found. Please go back and try again.</div>}
      <div className={styles.payCard}>
        <div className={styles.payRow}><span>Inspection Type</span><span>{inspectionType === 'home' ? '🏠 Home Inspection' : '🏢 Branch Inspection'}</span></div>
        <div className={styles.payRow}><span>Fee</span><span className={styles.payAmount}>₹{inspectionFee}</span></div>
        <div className={styles.payNote}>✓ Secure payment powered by Razorpay</div>
      </div>
      {error && <div className={styles.apiError}>⚠️ {error}</div>}
      <div className={styles.footerBtns} style={{ justifyContent: 'space-between', marginTop: 0 }}>
        <button className={styles.secondaryBtn} onClick={onBack}>← Back</button>
        <button className={styles.nextBtn} onClick={handlePay} disabled={loading || !inspectionId}>{loading ? 'Opening Razorpay…' : `Pay ₹${inspectionFee} →`}</button>
      </div>
    </div>
  );
}

function SummaryStep({ carData, inspectionData, bookingRef, razorpayPaymentId, inspectionFee, paymentMethod, onNewSell }) {
  const paymentInfo = (() => { try { return JSON.parse(localStorage.getItem('aucwini_last_payment') || '{}'); } catch { return {}; } })();
  const methodLabel = { upi: 'UPI', card: 'Card / Debit Card', netbanking: 'Net Banking' };
  return (
    <div className={styles.stepContent}>
      <div className={styles.successBanner}>
        <CheckCircle size={52} color="#22c55e" />
        <h2>Booking Confirmed! 🎉</h2>
        <p>Your car inspection has been scheduled &amp; payment received</p>
        {bookingRef && (<div className={styles.bookingRef}><span>Booking Reference</span><strong>{bookingRef}</strong></div>)}
        {razorpayPaymentId && <div style={{ marginTop: 8, fontSize: '.75rem', color: '#888' }}>Payment ID: {razorpayPaymentId}</div>}
      </div>
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <h4><Car size={15} /> Car Details</h4>
          {[['Brand', carData.car_brand],['Model', carData.model],['Year', carData.year],['Fuel', carData.fuel_type],['Transmission', carData.transmission],['KM Range', carData.km_range],['Ownership', carData.ownership],carData.registration_number && ['Reg No.', carData.registration_number]].filter(Boolean).map(([l, v]) => (<div key={l} className={styles.sumRow}><span>{l}</span><span>{v || '—'}</span></div>))}
        </div>
        <div className={styles.summaryCard}>
          <h4><Calendar size={15} /> Inspection Details</h4>
          {[['Type', inspectionData.inspection_type === 'home' ? 'Home Inspection' : 'Branch Inspection'],['Date', inspectionData.preferred_date ? new Date(inspectionData.preferred_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],['Time', TIME_LABELS[inspectionData.preferred_time]],['City', inspectionData.city],['Contact', inspectionData.full_name || inspectionData.contact_person],['Phone', inspectionData.contact_phone ? `+91 ${inspectionData.contact_phone}` : '—']].map(([l, v]) => (<div key={l} className={styles.sumRow}><span>{l}</span><span>{v}</span></div>))}
        </div>
        <div className={`${styles.summaryCard} ${styles.summaryCardPayment}`}>
          <h4>💳 Payment Details</h4>
          <div className={styles.sumRow}><span>Inspection Fee</span><span className={styles.sumPaid}>₹{inspectionFee || paymentInfo.amount}</span></div>
          <div className={styles.sumRow}><span>Payment Method</span><span>{methodLabel[paymentMethod || paymentInfo.method] || 'Online'}</span></div>
          <div className={styles.sumRow}><span>Status</span><span className={styles.sumStatusBadge}>✓ Paid</span></div>
          {razorpayPaymentId && <div className={styles.sumRow}><span>Transaction ID</span><span style={{ fontSize: '.72rem', wordBreak: 'break-all' }}>{razorpayPaymentId}</span></div>}
        </div>
      </div>
      <div className={styles.nextStepsList}>
        <h4>What happens next?</h4>
        {['Our expert will call to confirm within 2 hours','Inspector visits at your scheduled time','Get instant price offer after inspection','Accept offer and get paid immediately'].map((s, i) => (<div key={i} className={styles.nextStep}><span>{i+1}</span><p>{s}</p></div>))}
      </div>
      <div className={styles.ctaRow}><button className={styles.primaryBtn} onClick={onNewSell}>Sell Another Car</button><a href="/my-sells" className={styles.secondaryBtn}>View My Sells</a></div>
    </div>
  );
}

// ─── MAIN WIZARD ──────────────────────────────────────────────────────────────
export default function SellCarWizard() {
  const { city }  = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const cityName  = city ? city.charAt(0).toUpperCase() + city.slice(1) : 'Your City';
  const { updateUser } = useAuth();
  const isLoggedIn = useCallback(() => !!localStorage.getItem('authToken'), []);

  const savedDraft = (() => {
    localStorage.removeItem('aucwini_sell_draft');
    const key = getUserDraftKey();
    if (!key) return {};
    try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch { return {}; }
  })();

  const [carData,  setCarData]  = useState({ ...BLANK_CAR, ...(savedDraft.carData || {}), ...(location.state?.prefill || {}) });
  const [inspData, setInspData] = useState({ ...BLANK_INSP(cityName), ...(savedDraft.inspData || {}) });
  const [docs,     setDocs]     = useState(BLANK_DOCS);
  const [photos,   setPhotos]   = useState(BLANK_PHOTOS);

  const [step,              setStep]              = useState(location.state?.startStep || 1);
  const [sellRequest,       setSellRequest]       = useState(null);
  const [inspection,        setInspection]        = useState(null);
  const [loading,           setLoading]           = useState(false);
  const [apiError,          setApiError]          = useState('');
  const [validationErrors,  setValidationErrors]  = useState({});
  const [razorpayPaymentId, setRazorpayPaymentId] = useState('');
  const [paymentMethod,     setPaymentMethod]     = useState('');
  const [loginStep,         setLoginStep]         = useState('phone');
  const [phone,             setPhone]             = useState('');
  const [otp,               setOtp]               = useState(['','','','','','']);
  const [loginError,        setLoginError]        = useState('');
  const [loginLoading,      setLoginLoading]      = useState(false);
  const [countdown,         setCountdown]         = useState(0);
  const [isNewUser,         setIsNewUser]         = useState(false);

  useEffect(() => { loadRazorpayScript(); }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);
  useEffect(() => { if (Object.keys(validationErrors).length > 0) setValidationErrors({}); }, [inspData]);
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' && !e.newValue) {
        clearDraftStorage(); setCarData(BLANK_CAR); setInspData(BLANK_INSP(cityName));
        setStep(1); setSellRequest(null); setInspection(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [cityName]);
  useEffect(() => {
    const key = getUserDraftKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify({ carData, inspData }));
  }, [carData, inspData]);

  const TOTAL_ONLINE_STEPS = 9;
  const isHeroStep   = step <= TOTAL_ONLINE_STEPS;
  const currentGroup = STEP_GROUPS.findIndex(g => g.steps.includes(step));

  function updateCar(f, v)   { setCarData(p  => ({ ...p, [f]: v })); }
  function updateInsp(f, v)  { setInspData(p => ({ ...p, [f]: v })); }
  function updateDoc(f, v)   { setDocs(p  => ({ ...p, [f]: v })); }
  function updatePhoto(f, v) { setPhotos(p => ({ ...p, [f]: v })); }
  function goNext() { setStep(s => s + 1); setApiError(''); }
  function goBack() { step === 1 ? navigate(`/sell-car/${city}`) : setStep(s => s - 1); }
  function pickAndNext(f, v) { updateCar(f, v); setTimeout(goNext, 160); }
  function handleBrandModelSelect(brand, model) { setCarData(prev => ({ ...prev, car_brand: brand, model })); setTimeout(goNext, 350); }
  function handleTagClick(targetStep) { if (targetStep <= step) { setStep(targetStep); setApiError(''); } }

  const editableTags = TAG_STEP_MAP.filter(t => carData[t.field]).map(t => ({ ...t, value: carData[t.field] }));
  const showTags = editableTags.length > 0;

  function startCountdown() {
    setCountdown(30);
    const iv = setInterval(() => setCountdown(p => { if (p <= 1) { clearInterval(iv); return 0; } return p - 1; }), 1000);
  }

  async function handleSendOtp() {
    if (phone.length !== 10) { setLoginError('Enter a valid 10-digit number'); return; }
    setLoginLoading(true); setLoginError('');
    try {
      const { authAPI } = await import('../../services/api');
      try { const checkRes = await authAPI.checkUser(phone); setIsNewUser(!checkRes.data?.exists); } catch { setIsNewUser(false); }
      await authAPI.sendOtp(phone);
      setLoginStep('otp'); startCountdown();
    } catch (e) {
      setLoginError(e.response?.data?.error || e.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally { setLoginLoading(false); }
  }

  async function handleVerifyOtp() {
    const code = otp.join('');
    if (code.length !== 6) { setLoginError('Enter the complete 6-digit OTP'); return; }
    setLoginLoading(true); setLoginError('');
    try {
      const { authAPI } = await import('../../services/api');
      const res = await authAPI.verifyOtp(phone, code);
      const { user, token } = res.data;
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      updateUser(user);
      if (user.phone) updateInsp('contact_phone', user.phone.replace('+91', ''));
      if (user.name)  { updateInsp('contact_person', user.name); updateInsp('full_name', user.name); }
      if (user.email) updateInsp('email', user.email);
      // Pre-fill existing bank/personal details if user already has them
      if (user.whatsapp_number)     updateInsp('whatsapp_number',     user.whatsapp_number);
      if (user.pincode)             updateInsp('pincode',             user.pincode);
      if (user.account_holder_name) updateInsp('account_holder_name', user.account_holder_name);
      if (user.bank_name)           updateInsp('bank_name',           user.bank_name);
      if (user.account_number)      updateInsp('account_number',      user.account_number);
      if (user.ifsc_code)           updateInsp('ifsc_code',           user.ifsc_code);
      if (user.address)             updateInsp('current_address',     user.address);
      goNext();
    } catch (e) {
      setLoginError(e.response?.data?.error || 'Invalid OTP');
      setOtp(['','','','','','']);
    } finally { setLoginLoading(false); }
  }

  // ── Submit car details ────────────────────────────────────────────────────
async function submitCarDetails() {
  // NO setLoading here — handleContinueToPayment owns loading state
  setApiError('');
  try {
    const fd = new FormData();
    [
      ['car_brand',           carData.car_brand],
      ['model',               carData.model],
      ['year',                carData.year],
      ['km_driven',           kmRangeToNumber(carData.km_range)],
      ['price_expectation',   inspData.expected_price || carData.price_expectation],
      ['fuel_type',           carData.fuel_type    || ''],
      ['transmission',        carData.transmission || ''],
      ['ownership',           carData.ownership    || ''],
      ['km_range',            carData.km_range     || ''],
      ['registration_number', carData.registration_number || ''],
      ['price_negotiable',    inspData.price_negotiable || ''],
      ['loan_on_car',         inspData.loan_on_car || ''],
      ['accident_history',    inspData.accident_history || ''],
      ['extra_details',       inspData.extra_details || ''],
    ].forEach(([k, v]) => fd.append(k, v));

    const res = await api.post('/sell_requests', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (res.data.success) {
      setSellRequest(res.data.data);
      return res.data.data;
    }
    setApiError(res.data.errors?.[0] || 'Failed to submit car details');
    return null;
  } catch (e) {
    if (e.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      updateUser(null);
      setApiError('Your session expired. Please login again.');
      setStep(8);
      return null;
    }
    setApiError(
      e.response?.data?.errors?.[0] ||
      e.response?.data?.error ||
      e.message ||
      'Failed to submit car details'
    );
    return null;
  }
  // NO finally/setLoading here
}

  async function uploadDocsAndPhotos(sellRequestId) {
    const fd = new FormData();

    // Vehicle documents
    if (docs.rc_front)        fd.append('vehicle_documents[rc_front]',        docs.rc_front);
    if (docs.rc_back)         fd.append('vehicle_documents[rc_back]',          docs.rc_back);
    if (docs.insurance)       fd.append('vehicle_documents[insurance]',        docs.insurance);
    if (docs.puc)             fd.append('vehicle_documents[puc]',              docs.puc);
    if (docs.aadhar_front)    fd.append('vehicle_documents[aadhar_front]',     docs.aadhar_front);
    if (docs.aadhar_back)     fd.append('vehicle_documents[aadhar_back]',      docs.aadhar_back);
    if (docs.pan_card)        fd.append('vehicle_documents[pan_card]',         docs.pan_card);
    if (docs.service_history) fd.append('vehicle_documents[service_history]',  docs.service_history);

    // Car photos — only unique non-null files
    Object.values(photos).forEach(file => {
      if (file) fd.append('images[]', file);
    });

    try {
      await api.patch(`/sell_requests/${sellRequestId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      console.warn('Docs/photos upload failed (non-critical):', e);
    }
  }

  // ── KEY FIX: Save personal + bank details via update_seller_info ──────────
  // Sends plain JSON with { user: { ... } } — NOT FormData
  // Uses PATCH to be safe, but PUT also works (both routes exist)
// ── Save personal + bank details via update_seller_info ──────────────────
  async function savePersonalDetails() {
    // Build payload — always include all keys so controller's raw.key?() check works
    const payload = {
      user: {
        name:                 (inspData.full_name        || '').trim(),
        email:                (inspData.email            || '').trim(),
        address:              (inspData.current_address  || '').trim(),
        whatsapp_number:      (inspData.whatsapp_number  || '').trim(),
        pincode:              (inspData.pincode          || '').trim(),
        account_holder_name:  (inspData.account_holder_name || '').trim(),
        bank_name:            (inspData.bank_name        || '').trim(),
        account_number:       (inspData.account_number   || '').trim(),
        ifsc_code:            (inspData.ifsc_code        || '').trim().toUpperCase(),
      }
    };

    console.log('[savePersonalDetails] Sending payload:', JSON.stringify(payload, null, 2));

    try {
      const res = await api.put('/users/update_seller_info', payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('[savePersonalDetails] Response:', res.data);
      if (res.data?.success) {
        console.log('[savePersonalDetails] Saved successfully ✅');
        // Update localStorage user so pre-fill works on next visit
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          const updatedUser = { ...storedUser, ...res.data.user };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        } catch (_) {}
      } else {
        console.warn('[savePersonalDetails] Non-success response:', res.data);
      }
    } catch (e) {
      const status  = e.response?.status;
      const errMsg  = e.response?.data?.errors?.[0] || e.response?.data?.error || e.message;
      console.error('[savePersonalDetails] FAILED:', status, errMsg, e.response?.data);

      // Show in UI briefly — but do NOT block the sell flow
      setApiError(`⚠️ Could not save personal details (${status || 'network error'}): ${errMsg}. Continuing...`);
      setTimeout(() => setApiError(''), 5000);
      // Re-throw only for 401 (session expired) — all other errors are non-blocking
      if (status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setStep(8);
        throw e;
      }
    }
  }

  function buildValidationErrors() {
    const errors = {};
    if (!inspData.preferred_date)
      errors.preferred_date = 'Please select a date';
    if (inspData.inspection_type === 'home' && !inspData.seller_address?.trim())
      errors.seller_address = 'Please enter your address';
    if (inspData.inspection_type === 'branch' && !inspData.branch_location)
      errors.branch_location = 'Please select a branch';
    if (!inspData.city)
      errors.city = 'Please select a city';
    if (!inspData.expected_price)
      errors.expected_price = 'Please enter the expected price';
    if (!inspData.full_name?.trim())
      errors.full_name = 'Please enter your full name';
    if (!inspData.contact_phone || inspData.contact_phone.length !== 10)
      errors.contact_phone = 'Please enter a valid 10-digit mobile number';
    if (!inspData.email?.trim())
      errors.email = 'Please enter your email address';
    if (!inspData.current_address?.trim())
      errors.current_address = 'Current address enter karein';
    if (!inspData.pincode || inspData.pincode.length < 6)
      errors.pincode = 'Please enter a valid 6-digit pincode';
    if (!inspData.account_holder_name?.trim())
      errors.account_holder_name = 'Please enter the account holder name';
    if (!inspData.bank_name?.trim())
      errors.bank_name = 'Please enter the bank name';
    if (!inspData.account_number?.trim())
      errors.account_number = 'Please enter the account number';
    if (!inspData.ifsc_code?.trim())
      errors.ifsc_code = 'Please enter the IFSC code';
    else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(inspData.ifsc_code))
      errors.ifsc_code = 'Please enter a valid IFSC code — format: SBIN0001234';
    return errors;
  }

  async function bookInspection(srId) {
    setApiError('');
    try {
      const payload = {
        sell_request_id:      srId,
        inspection_type:      inspData.inspection_type,
        seller_address:       inspData.seller_address || '',
        branch_location:      inspData.branch_location || '',
        preferred_date:       inspData.preferred_date,
        preferred_time:       inspData.preferred_time,
        contact_person:       inspData.full_name || inspData.contact_person,
        contact_phone:        inspData.contact_phone,
        city:                 inspData.city,
        special_instructions: inspData.special_instructions || '',
      };
      const res = await api.post('/car_inspections', payload);
      const inspObj = res.data?.data || res.data;
      if (!inspObj?.id) {
        setApiError(res.data?.message || 'Inspection booking failed — no ID returned.');
        return null;
      }
      setInspection(inspObj);
      return inspObj;
    } catch (e) {
      setApiError(
        e.response?.data?.message ||
        e.response?.data?.errors?.[0] ||
        e.message ||
        'Failed to book inspection'
      );
      return null;
    }
  }

 async function handleContinueToPayment() {
    const errors = buildValidationErrors();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstKey = Object.keys(errors)[0];
      const el = document.getElementById(`ve-${firstKey}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setValidationErrors({});
    setLoading(true);
    try {
      // Run personal details save + sell request creation in parallel
      let sr = sellRequest;

      if (!sr) {
        // Save personal details and create sell request simultaneously
        const [, newSr] = await Promise.all([
          savePersonalDetails(),
          submitCarDetails(),
        ]);
        sr = newSr;
        if (!sr) return;
      } else {
        await savePersonalDetails();
      }

      // Upload docs + photos in ONE combined request
      await uploadDocsAndPhotos(sr.id);

      // Book inspection
      const insp = await bookInspection(sr.id);
      if (!insp) return;

      goNext();
    } catch (e) {
      // 401 already handled inside savePersonalDetails
      if (e?.response?.status !== 401) {
        setApiError(e.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  function docsValid()   { return !!(docs.rc_front && docs.insurance && docs.puc && docs.aadhar_front && docs.aadhar_back && docs.pan_card); }
  function photosValid() { return !!(photos.front_view && photos.rear_view && photos.left_side && photos.right_side && photos.dashboard && photos.odometer && photos.rear_seats); }

  function handleNewSell() {
    setStep(1); setSellRequest(null); setInspection(null);
    setCarData(BLANK_CAR); setInspData(BLANK_INSP(cityName));
    setDocs(BLANK_DOCS); setPhotos(BLANK_PHOTOS);
    setValidationErrors({}); clearDraftStorage();
  }

  const stepCounterDisplay = step <= TOTAL_ONLINE_STEPS ? `${step}/${TOTAL_ONLINE_STEPS}` : null;

  function renderStep() {
    switch (step) {
      case 1:  return <RegistrationFirstStep value={carData.registration_number} onChange={v => updateCar('registration_number', v)} onNext={goNext} />;
      case 2:  return <BrandModelStep brandValue={carData.car_brand} modelValue={carData.model} onSelect={handleBrandModelSelect} />;
      case 3:  return <YearStep value={carData.year} onChange={v => pickAndNext('year', v)} />;
      case 4:  return <ListStep question="Select the <strong>fuel type</strong>" options={FUEL_TYPES} value={carData.fuel_type} onChange={v => pickAndNext('fuel_type', v)} />;
      case 5:  return <ListStep question="Select the <strong>transmission type</strong>" options={TRANSMISSIONS} value={carData.transmission} onChange={v => pickAndNext('transmission', v)} />;
      case 6:  return <ListStep question="How many <strong>kilometres</strong> has your car run?" options={KM_RANGES} value={carData.km_range} onChange={v => pickAndNext('km_range', v)} />;
      case 7:  return <ListStep question="Select the <strong>ownership history</strong> of your car" options={OWNERSHIP} value={carData.ownership} onChange={v => pickAndNext('ownership', v)} />;
      case 8:
        if (isLoggedIn()) { setTimeout(goNext, 0); return <div className={styles.loading}>Loading…</div>; }
        return (<LoginGate phone={phone} otp={otp} loginStep={loginStep} onPhoneChange={v => { setPhone(v); setLoginStep('phone'); }} onOtpChange={setOtp} onSendOtp={handleSendOtp} onVerifyOtp={handleVerifyOtp} loading={loginLoading} error={loginError} countdown={countdown} onResend={handleSendOtp} isNewUser={isNewUser} />);
      case 9:  return (<ValuationStep brand={carData.car_brand} model={carData.model} year={carData.year} kmRange={carData.km_range} ownership={carData.ownership} onSchedule={goNext} />);
      case 10: return <DocumentsStep docs={docs} onChange={updateDoc} />;
      case 11: return <PhotosStep photos={photos} onChange={updatePhoto} />;
      case 12: return <InspectionStep formData={inspData} onChange={updateInsp} city={cityName} validationErrors={validationErrors} />;
      case 13: return (<PaymentStep inspectionType={inspData.inspection_type} inspectionFee={inspData.inspection_type === 'home' ? 500 : 250} inspectionId={inspection?.id} onPaymentSuccess={rzPayId => { setRazorpayPaymentId(rzPayId); clearDraftStorage(); goNext(); }} onBack={goBack} />);
      case 14: return (<SummaryStep carData={carData} inspectionData={inspData} bookingRef={inspection?.booking_reference} razorpayPaymentId={razorpayPaymentId} inspectionFee={inspData.inspection_type === 'home' ? 500 : 250} paymentMethod={paymentMethod} onNewSell={handleNewSell} />);
      default: return null;
    }
  }

  function renderFooter() {
    if ([1,2,3,4,5,6,7,8,9,13,14].includes(step)) return null;
    let label = 'Continue', handler = goNext, disabled = false;
    if (step === 10) { label = 'Next: Car Photos';         disabled = !docsValid() || loading; }
    if (step === 11) { label = 'Next: Inspection Details'; disabled = !photosValid() || loading; }
    if (step === 12) { label = 'Continue to Payment';      handler = handleContinueToPayment; disabled = loading; }
    return (
      <div className={styles.footerBtns}>
        <button className={styles.nextBtn} onClick={handler} disabled={disabled || loading}>
          {loading ? 'Processing…' : `${label} →`}
        </button>
      </div>
    );
  }

  function ProgressBar() {
    return (
      <div className={styles.progressWrap}>
        {STEP_GROUPS.map((g, i) => {
          const isDone = currentGroup > i;
          const isActive = currentGroup === i;
          return (
            <div key={g.label} className={`${styles.progressItem} ${isActive ? styles.active : ''} ${isDone ? styles.done : ''}`}>
              <div className={styles.progressDot}>{isDone && <CheckCircle size={16} color="#003566" />}</div>
              <span className={styles.progressLabel}>{g.label}</span>
              {i < STEP_GROUPS.length - 1 && <div className={`${styles.progressLine} ${isDone ? styles.doneLine : ''}`} />}
            </div>
          );
        })}
      </div>
    );
  }

  function TagBreadcrumb({ centered = false }) {
    if (!showTags) return null;
    return (
      <div className={`${styles.tagRow} ${centered ? styles.tagRowCentered : ''}`}>
        <span className={styles.tagLabel}>Your Car</span>
        {editableTags.map(t => (
          <button key={t.field} className={`${styles.tag} ${t.step < step ? styles.tagClickable : styles.tagCurrent}`} onClick={() => handleTagClick(t.step)} title={t.step < step ? `Edit ${t.label}` : undefined}>
            <span className={styles.tagFieldLabel}>{t.label}:</span>
            <span className={styles.tagValue}>{t.value}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.wizardWrap}>
      <div className={styles.stickyHeader}><ProgressBar /></div>
      {isHeroStep ? (
        <div className={styles.heroStepWrap}>
          <div className={styles.cardWrapper}>
            <TagBreadcrumb />
            <div className={styles.card}>
              <button className={styles.backBtn} onClick={goBack} aria-label="Go back"><ChevronLeft size={20} /></button>
              {stepCounterDisplay && <div className={styles.stepCounter}>{stepCounterDisplay}</div>}
              {apiError && <div className={styles.apiError}>⚠️ {apiError}</div>}
              <div className={styles.stepBody} key={step}>{renderStep()}</div>
              {renderFooter()}
            </div>
          </div>
          <div className={styles.heroSection}>
            <div className={styles.heroVisual}>
              <img src="/images/hero-combined.png" alt="Sell your car with Caryugo" className={styles.heroImage} />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.lateStepWrap}>
          <TagBreadcrumb centered />
          <div className={styles.cardNoHero}>
            <button className={styles.backBtn} onClick={goBack} aria-label="Go back"><ChevronLeft size={20} /></button>
            {apiError && <div className={styles.apiError}>⚠️ {apiError}</div>}
            <div className={styles.stepBody} key={step}>{renderStep()}</div>
            {renderFooter()}
          </div>
        </div>
      )}
    </div>
  );
}